/*
 * Copyright (c) 2020. American Express Company
 */

// third-party
import _ from 'lodash';
import React, { useEffect } from 'react';
import { LocalDate } from '@js-joda/core';
import { IconOpenBanking, Label, SelectOption } from '@americanexpress/dls-react';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

// services and models
import { DebtModel, DebtModelType } from 'src/types/debt.model';
import { DebtDrawerFormValues } from 'src/types/form-models/debt-drawer-form-values.model';
import { DebtDrawerViewModel } from 'src/types/view-models/debt-drawer.view.model';
import LiteralsService from 'src/services/literals/LiteralsService';
import NavigationService from 'src/services/navigation.service';
import UtilsService from 'src/services/utils.service';
import DebtService from 'src/services/debt.service';
import FormatService from 'src/services/format.service';

// components
import Drawer from 'src/components/drawer/Drawer';
import BasicInput from 'src/components/formik-fields/basic-input/BasicInput';
import CurrencyInput from 'src/components/formik-fields/currency-input/CurrencyInput';
import PercentInput from 'src/components/formik-fields/percent-input/PercentInput';
import Dropdown from '../formik-fields/dropdown/Dropdown';
import DateInput from 'src/components/formik-fields/date-input/DateInput';
import OptionsBox from 'src/components/options-box/OptionsBox';
import ConnectAccountBottomButtons from 'src/components/connect-account-bottom-buttons/ConnectAccountBottomButtons';

// styles
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import styles from 'src/components/debt-drawer/DebtDrawer.module.scss';

const durationLists: { yearly: number[]; yearlyMonths: number[]; monthly: number[] } = {
    yearly: [10, 15, 20, 30],
    yearlyMonths: [120, 180, 240, 360], // the month version of the above, backend expects months
    monthly: [12, 24, 36, 48, 60, 72, 84]
};

export const getInitialEndDate = (debt: DebtModel): LocalDate => {
    if (debt?.endDate && debt.endDate.toString() !== debt.startDate?.toString()) {
        return debt.endDate; // Only if endDate !== startDate do we treat it as a real endDate value
    }
    return null;
};

export const calculateInterestToSave = (interest: number): number => {
    return UtilsService.roundFloatNumber(interest / 100, 4);
};

export const calculateDurationToSave = (type: DebtModelType, duration: number): number => {
    if (type !== DebtModelType.Other) {
        return duration;
    }
    return 0;
};

export default function DebtDrawer({
    currentDebt,
    lastCashFlowEndDate,
    onStartupStep,
    handleBack,
    handleTopDrawer,
    handleAccounts,
    handleOpenConfirmation,
    handleReloadUser,
    history,
    firstImport,
    handleClearFirstImport,
    handleFirstImport,
    setDrawer,
    user
}: DebtDrawerViewModel): JSX.Element {
    const handleSuccess = async (): Promise<void> => {
        return await NavigationService.goBack(history);
    };

    async function goBack(): Promise<void> {
        if (onStartupStep) {
            handleTopDrawer();
        } else {
            await handleAccounts();
        }
    }

    const handleUpsert = _.once(async (values: DebtDrawerFormValues): Promise<void> => {
        const debt: DebtModel = {
            ...currentDebt,
            name: values.name,
            type: values.type,
            amount: values.amount,
            interest: calculateInterestToSave(values.interest),
            startDate: values.startDate,
            endDate: values.endDate,
            duration: calculateDurationToSave(values.type, values.duration)
        };

        if (debt.id) {
            await DebtService.updateDebt(debt);
        } else {
            await DebtService.createDebt(debt);
        }
        await handleReloadUser();
        await goBack();
    });

    const handleDelete = _.once(async (): Promise<void> => {
        await DebtService.deleteDebt(currentDebt);
        await handleReloadUser();
        await goBack();
    });

    const TypeSpecificDuration = (): JSX.Element => {
        const { values, resetForm, initialValues, isSubmitting } = useFormikContext<DebtDrawerFormValues>();

        useEffect(() => {
            if (!isSubmitting) {
                resetForm({
                    values: {
                        ...initialValues,
                        type: values.type
                    }
                });
            }
        }, [values.type]);

        switch (values.type) {
            case DebtModelType.Mortgage:
            case DebtModelType.Student:
                return (
                    <Dropdown
                        id="duration"
                        label={LiteralsService.getLiteral(`DebtDrawer.${values.type}.durationLabel`)}
                    >
                        {durationLists.yearly.map((y) => (
                            <option key={y} value={y * 12}>
                                {String(y) + ' year'}
                            </option>
                        ))}
                    </Dropdown>
                );
            case DebtModelType.Auto:
            case DebtModelType.Personal:
                return (
                    <Dropdown
                        id="duration"
                        label={LiteralsService.getLiteral(`DebtDrawer.${values.type}.durationLabel`)}
                    >
                        {durationLists.monthly.map((m) => (
                            <option key={m} value={m}>
                                {String(m) + ' months'}
                            </option>
                        ))}
                    </Dropdown>
                );
            default:
                return <></>;
        }
    };

    const currentDebtTypeDisplayName = (debtType: DebtModelType): string => {
        let displayName: string;
        if (currentDebt) {
            const types: Array<[string, DebtModelType]> = _.filter(
                DebtModel.debtTypes,
                (pair: [string, DebtModelType]) => pair[1] === debtType
            );
            if (types.length) {
                displayName = types[0][0];
            }
        }
        // as a last resort, display the debt type key (this should never happen)
        return displayName || '';
    };

    const DebtTypeHeader = (): JSX.Element => {
        const { values } = useFormikContext<DebtDrawerFormValues>();
        return (
            <div>
                <Label>{LiteralsService.getLiteral('DebtDrawer.debtTypeLabel')}</Label>
                <Typography variant={TypographyVariants.HEADING_M}>
                    {currentDebtTypeDisplayName(values.type)}
                </Typography>
            </div>
        );
    };

    return (
        <Drawer
            drawerProps={{ history }}
            handleBack={handleBack}
            title={LiteralsService.getLiteral('DebtDrawer.title')}
            className={styles.debtDrawer}
            data-cy="debtDrawer"
            withBackButton
        >
            <OptionsBox
                headerText={LiteralsService.getLiteral('DebtDrawer.ConnectAssetAccounts.header')}
                icon={<IconOpenBanking />}
                content={[
                    LiteralsService.getLiteral('DebtDrawer.ConnectAssetAccounts.content1'),
                    LiteralsService.getLiteral('ConnectAssetAccounts.content2')
                ]}
                isBlue
                hasPlaidButton
                handleSuccess={handleSuccess}
                firstImport={firstImport}
                handleAccounts={handleAccounts}
                handleClearFirstImport={handleClearFirstImport}
                handleFirstImport={handleFirstImport}
                setDrawer={setDrawer}
                user={user}
            />
            <Formik
                initialValues={{
                    type: currentDebt?.type,
                    name: currentDebt?.name || '',
                    amount: currentDebt?.amount,
                    startDate: currentDebt?.startDate || LocalDate.now(),
                    endDate: getInitialEndDate(currentDebt),
                    interest: UtilsService.roundFloatNumber((currentDebt?.interest || 0) * 100, 2),
                    duration: Number(currentDebt?.duration) || 0
                }}
                validationSchema={Yup.object({
                    type: Yup.mixed<DebtModelType>()
                        .required(LiteralsService.getLiteral('DebtDrawer.debtTypeError'))
                        .oneOf(DebtModel.debtTypeKeys, LiteralsService.getLiteral('DebtDrawer.debtTypeError')),
                    name: Yup.string().required(LiteralsService.getLiteral('DebtDrawer.nameInputError')),
                    amount: Yup.number()
                        .moreThan(0, LiteralsService.getLiteral('DebtDrawer.amountMustBeGreaterThanZeroError'))
                        .required(LiteralsService.getLiteral('DebtDrawer.amountInputError')),
                    startDate: Yup.date()
                        .required(LiteralsService.getLiteral('FormErrors.startDateRequired'))
                        .min(LocalDate.now().minusYears(100), LiteralsService.getLiteral('FormErrors.startDateMin'))
                        .max(lastCashFlowEndDate, LiteralsService.getLiteral('FormErrors.startDateMax')),
                    endDate: Yup.date()
                        .when('type', {
                            is: (type: DebtModelType) => type === DebtModelType.Other,
                            then: Yup.date()
                                .nullable()
                                .transform((current, original) => (original === '' ? null : current))
                                .min(
                                    Yup.ref('startDate'),
                                    LiteralsService.getLiteral('FormErrors.endDateAfterStartDate')
                                )
                                .max(lastCashFlowEndDate, LiteralsService.getLiteral('FormErrors.endDateMax'))
                        })
                        .when('type', {
                            is: (type: DebtModelType) => type !== DebtModelType.Other,
                            then: Yup.date().nullable()
                        }),
                    interest: Yup.number(),
                    duration: Yup.number()
                        .when('type', {
                            is: (type: DebtModelType) =>
                                type === DebtModelType.Mortgage || type === DebtModelType.Student,
                            then: Yup.number()
                                .required(LiteralsService.getLiteral('DebtDrawer.debtDurationError'))
                                .oneOf(
                                    durationLists.yearlyMonths,
                                    LiteralsService.getLiteral('DebtDrawer.debtDurationError')
                                )
                        })
                        .when('type', {
                            is: (type: DebtModelType) => type === DebtModelType.Auto || type === DebtModelType.Personal,
                            then: Yup.number()
                                .required(LiteralsService.getLiteral('DebtDrawer.debtDurationError'))
                                .oneOf(
                                    durationLists.monthly,
                                    LiteralsService.getLiteral('DebtDrawer.debtDurationError')
                                )
                        })
                        .when('type', {
                            is: (type: DebtModelType) =>
                                type === DebtModelType.CreditCard || type === DebtModelType.Other,
                            then: Yup.number().nullable()
                        })
                })}
                onSubmit={handleUpsert}
            >
                {({ values }) => (
                    <Form className={styles.form}>
                        {currentDebt ? (
                            <DebtTypeHeader />
                        ) : (
                            <Dropdown id="type" label={LiteralsService.getLiteral('DebtDrawer.debtTypeLabel')}>
                                {DebtModel.debtTypes.map((type: [string, DebtModelType]) => {
                                    return (
                                        <SelectOption key={type[1]} value={type[1]}>
                                            {type[0]}
                                        </SelectOption>
                                    );
                                })}
                            </Dropdown>
                        )}
                        {values.type && (
                            <>
                                <BasicInput id="name" label={LiteralsService.getLiteral('DebtDrawer.debtNameLabel')} />

                                <DateInput
                                    label={LiteralsService.getLiteral('DebtDrawer.startDateLabel')}
                                    id={'startDate'}
                                />
                                {values.type === DebtModelType.Other && (
                                    <DateInput
                                        label={LiteralsService.getLiteral('DebtDrawer.endDateLabel')}
                                        id={'endDate'}
                                    />
                                )}

                                <CurrencyInput
                                    id="amount"
                                    label={LiteralsService.getLiteral(
                                        `DebtDrawer.${FormatService.formatToCamelCase(values.type)}.amountLabel`
                                    )}
                                    hint={
                                        !(
                                            values.type === DebtModelType.CreditCard ||
                                            values.type === DebtModelType.Other
                                        )
                                            ? LiteralsService.getLiteral(
                                                  `DebtDrawer.${FormatService.formatToCamelCase(
                                                      values.type
                                                  )}.amountSublabel`
                                              )
                                            : null
                                    }
                                />

                                <PercentInput
                                    id="interest"
                                    label={LiteralsService.getLiteral('DebtDrawer.interestLabel')}
                                />

                                <TypeSpecificDuration />
                            </>
                        )}
                        <ConnectAccountBottomButtons
                            currentAccount={currentDebt}
                            type={'Debt'}
                            handleBack={handleBack}
                            handleOpenConfirmation={handleOpenConfirmation}
                            handleDelete={handleDelete}
                        />
                    </Form>
                )}
            </Formik>
        </Drawer>
    );
}
